import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Loader2 } from 'lucide-react';
import { ShopChrome, SignInPrompt } from '@/components/shop/ShopChrome';
import ProductCard, { ProductCardSkeleton, ProductGrid } from '@/components/shop/ProductCard';
import { EmptyState } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { refreshWishlist, useShopState } from '@/lib/shopStore';

const WishlistPage = () => {
  const { user, openAuthModal } = useAuth();
  const { wishlist } = useShopState();
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // refreshWishlist answers null when the request failed — that is an error to
  // show, not an empty wishlist.
  const load = useCallback(async () => {
    const list = await refreshWishlist();
    setFailed(list === null);
    setItems(list);
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const retry = async () => {
    setRetrying(true);
    await load();
    setRetrying(false);
  };

  if (!user) {
    return (
      <ShopChrome>
        <SignInPrompt icon={Heart} title="Your wishlist" message="Log in to see the products you saved." onLogin={openAuthModal} />
      </ShopChrome>
    );
  }

  // Un-hearting a card removes it from the page straight away.
  const shown = items && wishlist ? items.filter((p) => wishlist.has(p.id)) : items;

  return (
    <ShopChrome>
      <div className="lk-container py-5 sm:py-8">
        <h1 className="text-h2 text-ink-900">
          My wishlist
          {shown?.length > 0 && <span className="tabular ml-2 text-base font-medium text-ink-500">({shown.length})</span>}
        </h1>
        <div className="mt-5">
          {failed ? (
            <EmptyState
              icon={Heart}
              title="Your wishlist could not be loaded"
              message="Check your connection and try again."
              action={
                <button type="button" onClick={retry} disabled={retrying} className="lk-btn-primary">
                  {retrying && <Loader2 className="h-4 w-4 animate-spin" />}
                  Retry
                </button>
              }
            />
          ) : !shown ? (
            <ProductGrid>
              {Array.from({ length: 4 }, (_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </ProductGrid>
          ) : shown.length === 0 ? (
            <EmptyState
              icon={Heart}
              title="Your wishlist is empty"
              message="Tap the heart on any product to save it here."
              action={<Link to="/shop" className="lk-btn-primary">Explore the shop</Link>}
            />
          ) : (
            <ProductGrid>
              {shown.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </ProductGrid>
          )}
        </div>
      </div>
    </ShopChrome>
  );
};

export default WishlistPage;
